import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PersoonRow } from '@fleet/shared';
import { Badge, Button, Input } from '../../design-system';
import { AutoLink, PasLink, PersoonLink } from '../../components/ui/EntiteitLink';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { Modal } from '../../components/ui/Modal';
import { ZoekBalk } from '../../components/ui/ZoekBalk';
import { ApiError } from '../../lib/apiClient';
import { useCreatePersoon, usePersonen } from './personen.api';

const COLS = 'minmax(160px, 1fr) 132px 208px 120px';

/** De eerste van een rij, met "+N" voor de rest. */
function firstPlus(items: ReactNode[]): ReactNode {
  if (items.length === 0) return <span style={{ color: '#B4B4A9' }}>—</span>;
  return (
    <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
      {items[0]}
      {items.length > 1 && (
        <span style={{ fontSize: 11, fontWeight: 700, color: '#888888' }}>+{items.length - 1}</span>
      )}
    </span>
  );
}

export function PersonenPage() {
  const navigate = useNavigate();
  const { data: personen, isLoading } = usePersonen();
  const create = useCreatePersoon();

  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [naam, setNaam] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const filtered = useMemo(() => {
    const all = personen ?? [];
    const term = q.trim().toLowerCase();
    if (!term) return all;
    return all.filter((p) => {
      const hay = [
        p.naam,
        ...p.autos.map((a) => `${a.kenteken} ${a.auto}`),
        ...p.passen.map((pas) => pas.pasnummer),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(term);
    });
  }, [personen, q]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!naam.trim()) return;
    setError('');
    try {
      await create.mutateAsync({ naam: naam.trim(), email: email.trim() || null });
      setOpen(false);
      setNaam('');
      setEmail('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon persoon niet toevoegen');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Eyebrow>Personen</Eyebrow>
          <h2 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 900, color: '#111111' }}>
            Medewerkers, auto&apos;s &amp; passen.
          </h2>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="primary" onClick={() => setOpen(true)}>
            + Persoon toevoegen
          </Button>
          <Button variant="secondary" onClick={() => navigate('/personen/archief')}>
            Archief
          </Button>
        </div>
      </div>

      <ZoekBalk
        waarde={q}
        onWaarde={setQ}
        placeholder="Zoek op naam, kenteken, auto of pasnummer…"
        ariaLabel="Personen zoeken"
        aantal={
          filtered.length === (personen?.length ?? 0)
            ? `${filtered.length} personen`
            : `${filtered.length} van ${personen?.length ?? 0}`
        }
      />

      <div
        style={{
          background: '#FFFFFF',
          border: '1px solid #E3D9CC',
          borderRadius: 4,
          overflowX: 'auto',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: COLS,
            gap: 10,
            padding: '12px 20px',
            borderBottom: '2px solid #111111',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#666666',
            minWidth: 620,
          }}
        >
          <span>Naam</span>
          <span>Kenteken</span>
          <span>Pas</span>
          <span>Status</span>
        </div>
        {isLoading && <div style={{ padding: '24px 20px', color: '#888888' }}>Personen laden…</div>}
        {filtered.map((p: PersoonRow) => (
          <div
            key={p.id}
            onClick={() => navigate(`/personen/${p.id}`)}
            style={{
              display: 'grid',
              gridTemplateColumns: COLS,
              gap: 10,
              padding: '12px 20px',
              borderBottom: '1px solid #F0E8DC',
              fontSize: 13,
              alignItems: 'center',
              cursor: 'pointer',
              minWidth: 620,
            }}
          >
            <PersoonLink
              persoonId={p.id}
              naam={p.naam}
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            />
            {firstPlus(p.autos.map((a) => <AutoLink key={a.kenteken} kenteken={a.kenteken} />))}
            <span style={{ fontSize: 12 }}>
              {firstPlus(
                p.passen.map((pas) => (
                  <PasLink key={pas.id} pasId={pas.id} pasnummer={pas.pasnummer} />
                )),
              )}
            </span>
            <span>
              {p.actief ? (
                <Badge tone="success">Actief</Badge>
              ) : (
                <Badge tone="neutral">Inactief</Badge>
              )}
            </span>
          </div>
        ))}
        {!isLoading && filtered.length === 0 && (
          <div style={{ padding: '24px 20px', color: '#888888' }}>Geen personen gevonden.</div>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Persoon toevoegen">
        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            label="Naam"
            placeholder="Voor- en achternaam"
            value={naam}
            onChange={(e) => setNaam(e.target.value)}
            autoFocus
            required
          />
          <Input
            label="E-mail (optioneel)"
            type="email"
            placeholder="naam@veerenstael.nl"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {error && <div style={{ fontSize: 13, color: '#B0543B', fontWeight: 700 }}>{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <Button variant="ghost" type="button" onClick={() => setOpen(false)}>
              Annuleren
            </Button>
            <Button variant="primary" type="submit" disabled={create.isPending}>
              {create.isPending ? 'Bezig…' : 'Toevoegen'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
